import React, { useState, useEffect } from "react";
import { useNotix } from "../components/Notix/Notix";
import './TaskCalendar.css';
import ConfirmDialog from "./ConfirmDialog";
import {
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc
} from "firebase/firestore";
import { auth } from "../firebase";
import { db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const TaskCalendar = () => {
  const notix = useNotix();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [user, setUser] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [selectedDayTasks, setSelectedDayTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estado para controlar el diálogo de confirmación
  const [taskToDelete, setTaskToDelete] = useState(null);

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setAllTasks([]);
      setSelectedDayTasks([]);
      return;
    }

    const q = query(
      collection(db, "scheduledTasks"),
      where("userId", "==", user.uid)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const fetchedTasks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllTasks(fetchedTasks);
    }, (error) => {
      console.error("❌ Error en listener de tareas:", error);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    updateSelectedDayTasks();
  }, [selectedDate, allTasks]);

  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeDateString = (dateInput) => {
    if (!dateInput) return null;

    if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateInput;
    }

    if (dateInput instanceof Date) {
      return formatDateToString(dateInput);
    }

    if (dateInput && typeof dateInput.toDate === 'function') {
      return formatDateToString(dateInput.toDate());
    }

    if (typeof dateInput === 'string') {
      const parsed = new Date(dateInput);
      if (!isNaN(parsed.getTime())) {
        return formatDateToString(parsed);
      }
    }

    return null;
  };

  const updateSelectedDayTasks = () => {
    const selectedDateStr = formatDateToString(selectedDate);

    const dayTasks = allTasks.filter(task => {
      const taskDate = normalizeDateString(task.date);
      return taskDate === selectedDateStr;
    });

    dayTasks.sort((a, b) => {
      if (!a.time && b.time) return 1;
      if (a.time && !b.time) return -1;
      if (!a.time && !b.time) return 0;
      return a.time.localeCompare(b.time);
    });

    setSelectedDayTasks(dayTasks);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const hasTasksOnDate = (date) => {
    const dateStr = formatDateToString(date);
    return allTasks.some(task => {
      const taskDate = normalizeDateString(task.date);
      return taskDate === dateStr;
    });
  };

  const handleDateClick = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "Sin hora";

    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatSelectedDate = (date) => {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  // Abrimos diálogo de confirmación al querer borrar
  const handleDeleteTask = (taskId) => {
    setTaskToDelete(taskId);
  };

  // Confirmar eliminación
  const confirmDelete = async () => {
    if (!taskToDelete) return;

    try {
      await deleteDoc(doc(db, "scheduledTasks", taskToDelete));
      notix.info('Tarea eliminada correctamente', { position: 'top-center' });
    } catch (error) {
      console.error("Error al eliminar tarea:", error);
      notix.error('No se pudo eliminar la tarea', { position: 'top-center' });
    } finally {
      setTaskToDelete(null);
    }
  };

  // Cancelar eliminación
  const cancelDelete = () => {
    setTaskToDelete(null);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isSelected = selectedDate.toDateString() === date.toDateString();
      const isToday = new Date().toDateString() === date.toDateString();
      const hasTasks = hasTasksOnDate(date);

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${hasTasks ? 'has-tasks' : ''}`}
          onClick={() => handleDateClick(day)}
        >
          {day}
          {hasTasks && <div className="task-indicator"></div>}
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="calendar-container">
        <div className="calendar-loading">
          <div className="loading-icon">⏳</div>
          <p>Cargando calendario...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="calendar-container">
        <div className="calendar-empty">
          <div className="empty-icon">🔒</div>
          <h4>No autenticado</h4>
          <p>Inicia sesión para ver el calendario</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      {/* Header del calendario */}
      <div className="calendar-header">
        <button className="nav-button" onClick={() => navigateMonth(-1)}>←</button>
        <h3>{months[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
        <button className="nav-button" onClick={() => navigateMonth(1)}>→</button>
      </div>

      {/* Días de la semana */}
      <div className="calendar-weekdays">
        {daysOfWeek.map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      {/* Calendario */}
      <div className="calendar-grid">
        {renderCalendarDays()}
      </div>

      {/* Tareas del día seleccionado */}
      <div className="selected-day-tasks">
        <h4>{formatSelectedDate(selectedDate)}</h4>

        {selectedDayTasks.length === 0 ? (
          <div className="no-tasks">
            <div className="no-tasks-icon">📋</div>
            <p>No hay tareas para este día</p>
            {allTasks.length > 0 && (
              <small style={{ color: '#666', fontSize: '12px' }}>
                Total de tareas cargadas: {allTasks.length}
              </small>
            )}
          </div>
        ) : (
          <div className="tasks-list">
            {selectedDayTasks.map((task) => {
              const isCompleted = !!task.completed;
              // Comprobar si se marca fuera de fecha
              const taskDateStr = normalizeDateString(task.date);
              const todayStr = formatDateToString(new Date());
              const completedLate = !isCompleted && todayStr !== taskDateStr;

              return (
                <div key={task.id} className="task-item">
                  <div className="task-time">{formatTime(task.time)}</div>
                  <div className="task-title">{task.task}</div>
                  <button
                    className={isCompleted ? "undo-btn" : "complete-btn"}
                    onClick={async () => {
                      await updateDoc(doc(db, "scheduledTasks", task.id), {
                        completed: !isCompleted,
                        // Si se marca como completada y no es el día de la tarea, marca como completada tarde
                        completedLate: !isCompleted ? completedLate : false
                      });
                      notix.info(
                        !isCompleted
                          ? (completedLate
                              ? "Tarea marcada como completada fuera de fecha"
                              : "Tarea marcada como completada")
                          : "Tarea marcada como no completada",
                        { position: "top-center" }
                      );
                    }}
                    aria-label={
                      isCompleted
                        ? `Marcar como no completada la tarea ${task.task}`
                        : `Marcar como completada la tarea ${task.task}`
                    }
                  >
                    {isCompleted ? "↩️" : "✅"}
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDeleteTask(task.id)}
                    aria-label={`Eliminar tarea ${task.task}`}
                  >
                    ❌
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmación para eliminar tarea */}
      <ConfirmDialog
        open={Boolean(taskToDelete)}
        message="¿Seguro que quieres eliminar esta tarea?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default TaskCalendar;
